import { createFileRoute, Link } from "@tanstack/react-router";
import { AcademyNav } from "@/components/academy/AcademyNav";
import { Crown, Check } from "lucide-react";
import { ACADEMY_ID, breadcrumbs, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

const TITLE = "Academy Premium — Unlock the Full Canon | Buddha KA";
const DESCRIPTION =
  "Academy Premium opens the master-tier canon: advanced lessons, interactive bodygraph and knowledge-graph tools, enhanced AI guidance, and certification tracks.";

export const Route = createFileRoute("/academy/premium")({
  head: () => ({
    ...seoMeta({ title: TITLE, description: DESCRIPTION, path: "/academy/premium" }),
    scripts: [
      ldScript(
        pageNode({
          type: "WebPage",
          id: "https://buddhaka.com/academy/premium#premium",
          url: "https://buddhaka.com/academy/premium",
          name: TITLE,
          description: DESCRIPTION,
          about: [personRef(), orgRef()],
          extra: { isPartOf: { "@id": ACADEMY_ID } },
        }),
      ),
      ldScript(breadcrumbs([["Academy", "/academy"], ["Premium", "/academy/premium"]])),
    ],
  }),
  component: PremiumPage,
});


const includes = [
  "Full library of Master-tier lessons across every domain",
  "Interactive tools: Bodygraph deep dive, Knowledge Graph explorer",
  "Enhanced AI Guide with lesson-scoped grounding",
  "Downloadable meditations, wallpapers, and workbooks",
  "Certification tracks with verifiable completion",
  "Priority access to new domains as they release",
];

function PremiumPage() {
  return (
    <>
      <AcademyNav />
      <div className="min-h-dvh bg-obsidian text-foreground">
        <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold flex items-center gap-2"><Crown size={14}/> Premium</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-6xl text-gold-luminous">The Academy, unlocked.</h1>
          <p className="mt-5 max-w-2xl text-lg text-foreground/75">
            Move from student to practitioner. Premium opens the advanced canon, deeper tools, and a personal AI guide trained on the full corpus.
          </p>

          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            <article className="rounded-2xl border hairline p-6">
              <p className="text-xs uppercase tracking-widest text-foreground/60">Free</p>
              <p className="mt-2 font-serif text-3xl">$0</p>
              <ul className="mt-4 space-y-2 text-sm text-foreground/80">
                <li className="flex gap-2"><Check size={14} className="mt-1 text-gold shrink-0" /> Beginner & Intermediate lessons</li>
                <li className="flex gap-2"><Check size={14} className="mt-1 text-gold shrink-0" /> Daily practice & journal</li>
                <li className="flex gap-2"><Check size={14} className="mt-1 text-gold shrink-0" /> Progress tracking & streaks</li>
                <li className="flex gap-2"><Check size={14} className="mt-1 text-gold shrink-0" /> Basic AI Guide</li>
              </ul>
              <Link to="/academy" className="mt-6 inline-flex items-center rounded-full border hairline px-5 py-2.5 text-sm">Start free</Link>
            </article>
            <article className="rounded-2xl border border-gold bg-gold/5 p-6 shadow-gold">
              <p className="text-xs uppercase tracking-widest text-gold">Premium</p>
              <p className="mt-2 font-serif text-3xl text-gold-luminous">$18<span className="text-base text-foreground/60">/mo</span></p>
              <ul className="mt-4 space-y-2 text-sm text-foreground/85">
                {includes.map((i) => (
                  <li key={i} className="flex gap-2"><Check size={14} className="mt-1 text-gold shrink-0" /> {i}</li>
                ))}
              </ul>
              <Link to="/shop" className="mt-6 inline-flex items-center rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian">
                Upgrade to Premium
              </Link>
              <p className="mt-3 text-[10px] uppercase tracking-widest text-foreground/50">Cancel any time. Access retained through the current period.</p>
            </article>
          </div>

          <p className="mt-10 text-xs text-foreground/50">
            All Premium access is delivered through your KAfrequency account and honored on web, iOS, and Android.
          </p>
        </section>
      </div>
    </>
  );
}
